/** Cognito OAuth (PKCE) 認証ユーティリティ — payload-alpha と同じ User Pool を共有 */

type AuthConfig = {
  domain: string;
  clientId: string;
  redirectUri: string;
};

const getAuthConfig = (): AuthConfig => ({
  domain: import.meta.env.VITE_COGNITO_DOMAIN,
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
  redirectUri: `${window.location.origin}/auth/callback`,
});

const createTokenStore = () => {
  let idToken: string | null = null;
  let accessToken: string | null = null;

  return {
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
    setTokens: (tokens: { idToken: string; accessToken: string }) => {
      idToken = tokens.idToken;
      accessToken = tokens.accessToken;
    },
    clear: () => {
      idToken = null;
      accessToken = null;
    },
  } as const;
};

export const tokenStore = createTokenStore();

const base64UrlEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

const generateCodeVerifier = (): string => {
  const buffer = new Uint8Array(64);
  crypto.getRandomValues(buffer);
  return base64UrlEncode(buffer.buffer);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoded = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", encoded);
  return base64UrlEncode(hash);
};

const getLoginUrl = (codeChallenge: string): string => {
  const config = getAuthConfig();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: "openid email profile",
    identity_provider: "Google",
    code_challenge_method: "S256",
    code_challenge: codeChallenge,
  });
  return `${config.domain}/oauth2/authorize?${params.toString()}`;
};

type TokenResponse = {
  id_token: string;
  access_token: string;
  refresh_token: string;
};

export const exchangeCodeForTokens = async (
  code: string,
  codeVerifier: string,
): Promise<void> => {
  const config = getAuthConfig();
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
    code_verifier: codeVerifier,
  });

  const res = await fetch(`${config.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!res.ok) {
    throw new Error(`トークン取得に失敗しました: ${res.status}`);
  }

  const tokens = (await res.json()) as TokenResponse;
  tokenStore.setTokens({
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
  });
  localStorage.setItem("refresh_token", tokens.refresh_token);
};

export const refreshTokens = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return false;

  try {
    const config = getAuthConfig();
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: refreshToken,
    });
    const res = await fetch(`${config.domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) return false;

    const tokens = (await res.json()) as Omit<TokenResponse, "refresh_token">;
    tokenStore.setTokens({
      idToken: tokens.id_token,
      accessToken: tokens.access_token,
    });
    return true;
  } catch {
    return false;
  }
};

export const getIdToken = (): string | null => tokenStore.getIdToken();

export const logout = (): void => {
  tokenStore.clear();
  localStorage.removeItem("refresh_token");
};

export const startLogin = async (): Promise<void> => {
  const verifier = generateCodeVerifier();
  sessionStorage.setItem("code_verifier", verifier);
  const challenge = await generateCodeChallenge(verifier);
  window.location.href = getLoginUrl(challenge);
};

export const isAuthenticated = (): boolean => tokenStore.getIdToken() !== null;

/** local 開発時は Cognito をバイパスする */
export const isAuthBypassed = (): boolean =>
  import.meta.env.VITE_STAGE === "local";
